import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Checkbox, Descriptions, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, message } from "antd";
import { PageHeader } from "../../components/PageHeader";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { ApprovalTimeline } from "../../components/ApprovalTimeline";
import { LineItemsEditor, type LineItem } from "../../components/LineItemsEditor";
import { SiteLabel } from "../../components/SiteLabel";
import { SiteSelect } from "../../components/SiteSelect";
import { useApi } from "../../helpers/useApi";
import { hasPermission, hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useSitesQuery, useStockLocationsQuery } from "../../helpers/queries";
import type { SiteReturn, SiteReturnLine, SiteReturnDispositionType } from "../../api-models";

// Every received unit must be assigned to one of these before the return can complete
// (spec §20/§31.23) — the same "full quantity accounted for" gate the backend enforces.
const DISPOSITIONS: { value: SiteReturnDispositionType; label: string; help: string }[] = [
  { value: "RESTOCK", label: "Restock", help: "Back into Central Warehouse stock as usable." },
  { value: "QUARANTINE", label: "Quarantine", help: "Damaged or unavailable — held out of Available." },
  { value: "TRANSFER", label: "Transfer", help: "Straight on to another location." },
  { value: "SUPPLIER_RETURN", label: "Supplier return", help: "Reserved at Return Holding for a Supplier Return document." },
  { value: "WRITE_OFF", label: "Write off", help: "Unrecoverable — posted to the loss sink. Requires a reason." },
];

function dispositionedQty(line: SiteReturnLine): number {
  return (line.dispositions ?? []).reduce((sum, d) => sum + d.qty, 0);
}

export default function ReturnDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: sites = [] } = useSitesQuery();
  const { data: locations = [] } = useStockLocationsQuery();

  const isNew = id === "new";
  const [ret, setRet] = useState<SiteReturn | null>(null);
  const [items, setItems] = useState<LineItem[]>([]);
  const [siteId, setSiteId] = useState<number>();
  const [fromQuarantine, setFromQuarantine] = useState(false);
  const [reason, setReason] = useState("");
  const [dispositionLine, setDispositionLine] = useState<SiteReturnLine | null>(null);
  const [form] = Form.useForm();

  async function load() {
    if (isNew) return;
    const res = await request<SiteReturn>({ url: `/site-returns/${id}` });
    setRet(res);
    setReason(res.reason ?? "");
    setItems((res.lines ?? []).map((l) => ({ key: String(l.id), itemId: l.itemId, qty: l.qty })));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canWrite = hasPermission(user, "Returns.Write", ret?.siteId ?? siteId);
  const canExecute = hasAnyGrant(user, "Returns.Execute");
  const editable = isNew || ret?.status === "DRAFT";

  async function save() {
    const payload = {
      siteId: isNew ? siteId : ret!.siteId,
      // Only meaningful on create - the server keeps the source fixed once raised.
      fromQuarantine: isNew ? fromQuarantine : ret!.fromQuarantine,
      reason,
      items: items.filter((it) => it.itemId && it.qty).map((it) => ({ itemId: it.itemId, qty: it.qty })),
    };
    if (isNew) {
      const created = await request<SiteReturn>({ method: "POST", url: "/site-returns", data: payload });
      message.success("Site return created.");
      navigate(`/returns/${created.id}`, { replace: true });
    } else {
      await request({ method: "PUT", url: `/site-returns/${id}`, data: payload });
      message.success("Site return updated.");
      load();
    }
  }

  async function act(action: string) {
    await request({ method: "POST", url: `/site-returns/${id}/${action}` });
    message.success("Done.");
    load();
  }

  async function submitDisposition() {
    const values = await form.validateFields();
    await request({ method: "POST", url: `/site-returns/${id}/lines/${dispositionLine!.id}/dispositions`, data: values });
    message.success("Disposition recorded.");
    setDispositionLine(null);
    form.resetFields();
    load();
  }

  if (!isNew && !ret) return null;

  const outstanding = (ret?.lines ?? []).reduce((sum, l) => sum + (l.receivedQty - dispositionedQty(l)), 0);

  return (
    <div>
      <PageHeader
        title={isNew ? "New Site Return" : ret!.docNo}
        subtitle={ret ? <>Site: <SiteLabel site={ret.site} fallback={ret.siteId} /></> : undefined}
        extra={
          <Space wrap>
            {ret && (
              <span data-tour="doc-status">
                <DocumentStatusTag status={ret.status} />
              </span>
            )}
            {editable && canWrite && (
              <Button type="primary" onClick={save} loading={loading} data-tour="return-save">
                Save
              </Button>
            )}
            {!isNew && ret?.status === "DRAFT" && canWrite && (
              <Button onClick={() => act("submit")} data-tour="return-submit">
                Submit for approval
              </Button>
            )}
            {!isNew && (ret?.status === "DRAFT" || ret?.status === "PENDING_APPROVAL") && canWrite && (
              <Button danger onClick={() => act("cancel")}>
                Cancel
              </Button>
            )}
            {ret?.status === "APPROVED" && canExecute && (
              <Button onClick={() => act("dispatch")} data-tour="return-dispatch">
                Dispatch to Central
              </Button>
            )}
            {ret?.status === "DISPATCHED" && canExecute && (
              <Button onClick={() => act("receive")} data-tour="return-receive">
                Receive at Central
              </Button>
            )}
          </Space>
        }
      />

      {isNew && fromQuarantine && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Returning quarantined stock"
          description="Lines are checked against the site's quarantine balance rather than its store, and travel through transit to Return Holding like any other return. Decide there whether each unit is restocked, re-quarantined at Central, sent back to the supplier or written off."
        />
      )}
      {ret?.status === "DISPATCHED" && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="In transit"
          description="The stock has left the site and sits in this return's own in-transit location until Central receives it."
        />
      )}
      {ret?.status === "RECEIVED" && outstanding > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${outstanding} unit(s) still to be accounted for`}
          description="Every received unit needs a disposition before the return can complete."
        />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Site">
            {isNew ? (
              <SiteSelect
                data-tour="return-site"
                style={{ width: 220 }}
                options={sites.map((s) => ({ value: s.id, label: s.name }))}
                value={siteId}
                onChange={setSiteId}
                placeholder="Select a site"
              />
            ) : (
              <SiteLabel site={ret!.site} />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Reason">
            {editable && canWrite ? (
              <Input style={{ width: 260 }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this coming back?" data-tour="return-reason" />
            ) : (
              ret?.reason || "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Source" span={2}>
            {isNew && canWrite ? (
              <Checkbox checked={fromQuarantine} onChange={(e) => setFromQuarantine(e.target.checked)}>
                Return damaged stock out of this site's quarantine
              </Checkbox>
            ) : ret?.fromQuarantine ? (
              <Tag color="orange">Site quarantine</Tag>
            ) : (
              "Site store"
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {editable ? (
        <Card size="small" title="Items to return">
          <LineItemsEditor items={items} onChange={setItems} readOnly={!canWrite} />
        </Card>
      ) : (
        <Card size="small" title="Lines">
          <Table
            rowKey="id"
            dataSource={ret?.lines ?? []}
            pagination={false}
            columns={[
              { title: "Code", render: (_, r) => r.item?.code },
              { title: "Item", render: (_, r) => r.item?.name },
              { title: "Returned", dataIndex: "qty" },
              { title: "Received", dataIndex: "receivedQty" },
              { title: "Accounted for", render: (_, r) => dispositionedQty(r) },
              {
                title: "Dispositions",
                render: (_, r) => (
                  <Space wrap size={[4, 4]}>
                    {(r.dispositions ?? []).map((d) => (
                      <Tag key={d.id}>{`${d.type.replace(/_/g, " ")} ${d.qty}`}</Tag>
                    ))}
                    {(r.dispositions ?? []).length === 0 && "—"}
                  </Space>
                ),
              },
              {
                title: "",
                render: (_, r) =>
                  ret?.status === "RECEIVED" && canExecute && r.receivedQty - dispositionedQty(r) > 0.0000001 ? (
                    <Button
                      size="small"
                      data-tour="return-dispose"
                      onClick={() => {
                        setDispositionLine(r);
                        form.setFieldsValue({ type: "RESTOCK", qty: r.receivedQty - dispositionedQty(r), reason: "" });
                      }}
                    >
                      Dispose
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}

      {!isNew && <ApprovalTimeline docType="SITE_RETURN" docId={Number(id)} refreshKey={ret?.status} onActed={load} />}

      <Modal
        title="Record disposition"
        open={!!dispositionLine}
        onCancel={() => setDispositionLine(null)}
        onOk={submitDisposition}
        confirmLoading={loading}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="Disposition" rules={[{ required: true }]}>
            <Select options={DISPOSITIONS.map((d) => ({ value: d.value, label: d.label }))} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const type = form.getFieldValue("type") as SiteReturnDispositionType | undefined;
              const meta = DISPOSITIONS.find((d) => d.value === type);
              return meta ? <Alert type="info" showIcon style={{ marginBottom: 16 }} message={meta.help} /> : null;
            }}
          </Form.Item>
          <Form.Item name="qty" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() =>
              form.getFieldValue("type") === "TRANSFER" ? (
                <Form.Item name="destLocationId" label="Destination location" rules={[{ required: true }]}>
                  <Select options={locations.map((l) => ({ value: l.id, label: l.name }))} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => (
              <Form.Item
                name="reason"
                label="Reason"
                rules={[{ required: form.getFieldValue("type") === "WRITE_OFF", message: "A reason is required to write stock off." }]}
              >
                <Input placeholder="Why this outcome?" />
              </Form.Item>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
